import React, { useState, useEffect, useCallback } from 'react';
import {
    LayoutDashboard, MessageSquare, TrendingUp, BarChart3, Lightbulb,
    ArrowLeft, HeartPulse, Users, DatabaseZap, Sparkles, DollarSign, Library, Activity, ShieldCheck
} from 'lucide-react';
import saigeLogo from '../assets/saige-logo.png';

// Existing admin sub-pages (ops-focused)
import AdminFeedback from './AdminFeedback';

// New analytics components
import AnalyticsFilterBar from './analytics/AnalyticsFilterBar';
import OverviewTab from './analytics/OverviewTab';
import HealthTab from './analytics/HealthTab';
import UsageTab from './analytics/UsageTab';
import FunnelTab from './analytics/FunnelTab';
import RetentionTab from './analytics/RetentionTab';
import EventExplorerTab from './analytics/EventExplorerTab';
import FeedbackIntelligenceTab from './analytics/FeedbackIntelligenceTab';
import InsightsTab from './analytics/InsightsTab';
import MonetizationTab from './analytics/MonetizationTab';
import WorkflowTab from './analytics/WorkflowTab';
import CampusIntelligenceAnalyticsTab from './analytics/CampusIntelligenceAnalyticsTab';
import CampusIntelligenceTab from './analytics/CampusIntelligenceTab';
import LiveOpsTab from './analytics/LiveOpsTab';
import FeedbackInsightsTab from './analytics/FeedbackInsightsTab';
import OperatingStateTab from './analytics/OperatingStateTab';
import { useFeatureArea } from '../hooks/useTelemetry';

/**
 * AdminWorkspace — Dedicated route-based admin workspace shell.
 *
 * Routes:
 *   /admin                → Overview (analytics)
 *   /admin/health         → System Health (analytics)
 *   /admin/usage          → Feature Usage + time-spent (analytics)
 *   /admin/workflow       → Workflow Analytics (analytics)
 *   /admin/funnel         → Activation Funnel (analytics)
 *   /admin/retention      → Retention (analytics)
 *   /admin/events         → Event Explorer (analytics)
 *   /admin/monetization   → Monetization (analytics)
 *   /admin/ci-analytics   → Campus Intelligence Analytics (analytics)
 *   /admin/feedback       → Feedback Triage (ops)
 *   /admin/signals        → Signals & Insights — merged from feedback-intel + insights (analytics)
 *   /admin/campus-intelligence → Campus Intelligence (ops)
 */

let isCIEnabled = false;
try {
    const flags = JSON.parse(localStorage.getItem('saige_feature_flags') || '{}');
    // Gate CI Analytics tab on the master CI flag (admin UI flag or master CI flag).
    // This prevents the tab being visible when CI is disabled in production.
    isCIEnabled = !!(flags.campus_intelligence_admin_ui || flags.campus_intelligence);
} catch { /* flags default to off */ }

const NAV_ITEMS = [
    // ── Analytics ──
    { id: 'overview',       path: '/admin',                label: 'Overview',          icon: LayoutDashboard, group: 'analytics' },
    { id: 'operating-state', path: '/admin/operating-state', label: 'Operating State', icon: ShieldCheck,      group: 'analytics' },
    { id: 'live-ops',       path: '/admin/live-ops',       label: 'Live Ops',          icon: Activity,        group: 'analytics' },
    { id: 'health',         path: '/admin/health',         label: 'Health',            icon: HeartPulse,      group: 'analytics' },
    { id: 'usage',          path: '/admin/usage',          label: 'Feature Usage',     icon: BarChart3,       group: 'analytics' },
    { id: 'workflow',       path: '/admin/workflow',       label: 'Workflows',         icon: TrendingUp,      group: 'analytics' },
    { id: 'funnel',         path: '/admin/funnel',         label: 'Funnel',            icon: TrendingUp,      group: 'analytics' },
    { id: 'retention',      path: '/admin/retention',      label: 'Retention',         icon: Users,           group: 'analytics' },
    { id: 'events',         path: '/admin/events',         label: 'Event Explorer',    icon: DatabaseZap,     group: 'analytics' },
    { id: 'monetization',   path: '/admin/monetization',   label: 'Monetization',      icon: DollarSign,      group: 'analytics' },
    // ── Feedback & Intelligence ──
    { id: 'feedback',  path: '/admin/feedback', label: 'Feedback Triage',   icon: MessageSquare, group: 'feedback' },
    { id: 'signals',   path: '/admin/signals',  label: 'Signals & Insights', icon: Sparkles,     group: 'feedback' },
    // Legacy routes kept for deep-link compatibility but not shown in nav
    // feedback-intel and insights are merged into 'signals'
];

// CI Analytics tab: only visible when CI feature flag is enabled
if (isCIEnabled) {
    NAV_ITEMS.splice(8, 0, { id: 'ci-analytics', path: '/admin/ci-analytics', label: 'CI Analytics', icon: Library, group: 'analytics' });
    NAV_ITEMS.push({ id: 'campus-intelligence', path: '/admin/campus-intelligence', label: 'Campus Intelligence', icon: Library, group: 'feedback' });
}

/** Derive section ID from current pathname. */
const sectionFromPath = (pathname) => {
    const clean = pathname.replace(/\/+$/, '');
    const match = NAV_ITEMS.find(n => n.path === clean);
    return match ? match.id : 'overview';
};

const AdminWorkspace = ({ onBack }) => {
    const [activeSection, setActiveSection] = useState(() =>
        sectionFromPath(window.location.pathname)
    );

    // Feature area tracking: whole admin workspace + active tab
    useFeatureArea('admin_workspace');
    useFeatureArea(activeSection ? `admin_${activeSection}` : null);

    // ── Global filter state ──
    const [filters, setFilters] = useState({
        days: 7,
        compare: false,
        district_id: null,
        campus_id: null,
    });

    const updateFilters = useCallback((partial) => {
        setFilters(prev => ({ ...prev, ...partial }));
    }, []);

    // Force all tabs to reload on manual refresh
    const [refreshKey, setRefreshKey] = useState(0);
    const handleRefresh = useCallback(() => {
        setRefreshKey(k => k + 1);
    }, []);

    // Listen to popstate so back/forward updates the active section
    useEffect(() => {
        const handlePopState = () => {
            setActiveSection(sectionFromPath(window.location.pathname));
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // Navigate to a section via pushState
    const navigateTo = useCallback((item) => {
        window.history.pushState({}, '', item.path);
        setActiveSection(item.id);
    }, []);

    // Should we show the filter bar? (All analytics tabs)
    const showFilterBar = NAV_ITEMS.find(n => n.id === activeSection)?.group !== undefined;

    const renderContent = () => {
        const key = refreshKey;
        switch (activeSection) {
            case 'overview':       return <OverviewTab key={key} filters={filters} />;
            case 'operating-state': return <OperatingStateTab key={key} filters={filters} />;
            case 'live-ops':       return <LiveOpsTab key={key} filters={filters} />;
            case 'health':         return <HealthTab key={key} filters={filters} />;
            case 'usage':          return <UsageTab key={key} filters={filters} />;
            case 'workflow':       return <WorkflowTab key={key} filters={filters} />;
            case 'funnel':         return <FunnelTab key={key} filters={filters} />;
            case 'retention':      return <RetentionTab key={key} filters={filters} />;
            case 'events':         return <EventExplorerTab key={key} filters={filters} />;
            case 'monetization':   return <MonetizationTab key={key} filters={filters} />;
            case 'ci-analytics':   return <CampusIntelligenceAnalyticsTab key={key} filters={filters} />;
            case 'feedback':       return <AdminFeedback />;
            case 'feedback-intel': return <FeedbackIntelligenceTab key={key} filters={filters} />; // legacy deep-link
            case 'insights':       return <InsightsTab key={key} filters={filters} />; // legacy deep-link
            case 'signals':        return <FeedbackInsightsTab key={key} filters={filters} />;
            case 'campus-intelligence': return <CampusIntelligenceTab key={key} />;
            default:               return <OverviewTab key={key} filters={filters} />;
        }
    };

    // Group nav items for visual separation
    const analyticsItems = NAV_ITEMS.filter(n => n.group === 'analytics');
    const feedbackItems = NAV_ITEMS.filter(n => n.group === 'feedback');

    return (
        <div style={{
            display: 'flex', minHeight: '100vh', background: '#f8fafc',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}>
            {/* ── Sidebar ── */}
            <aside style={{
                width: 240, flexShrink: 0, background: '#0f172a',
                display: 'flex', flexDirection: 'column',
                borderRight: '1px solid #1e293b',
            }}>
                {/* Brand */}
                <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #1e293b' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: 4, borderRadius: 10,
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid rgba(255,255,255,0.08)',
                        }}>
                            <img
                                src={saigeLogo}
                                alt="Saige"
                                style={{
                                    height: 26, width: 'auto',
                                    objectFit: 'contain', display: 'block',
                                }}
                            />
                        </div>
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#f1f5f9' }}>
                                Saige Admin
                            </div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>
                                Intelligence Dashboard
                            </div>
                        </div>
                    </div>
                </div>

                {/* Nav Items */}
                <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Analytics section header */}
                    <div style={{
                        fontSize: 10, fontWeight: 600, color: '#475569',
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                        padding: '8px 14px 4px',
                    }}>
                        Analytics
                    </div>
                    {analyticsItems.map(item => (
                        <NavButton
                            key={item.id}
                            item={item}
                            isCurrent={activeSection === item.id}
                            onClick={() => navigateTo(item)}
                        />
                    ))}

                    {/* Divider */}
                    <div style={{
                        height: 1, background: '#1e293b',
                        margin: '8px 12px',
                    }} />

                    {/* Feedback & Intelligence section header */}
                    <div style={{
                        fontSize: 10, fontWeight: 600, color: '#475569',
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                        padding: '8px 14px 4px',
                    }}>
                        Intelligence
                    </div>
                    {feedbackItems.map(item => (
                        <NavButton
                            key={item.id}
                            item={item}
                            isCurrent={activeSection === item.id}
                            onClick={() => navigateTo(item)}
                        />
                    ))}
                </nav>

                {/* Back to Dashboard */}
                <div style={{ padding: '12px 10px', borderTop: '1px solid #1e293b' }}>
                    <button
                        onClick={onBack}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            width: '100%', padding: '10px 14px', borderRadius: 8,
                            border: 'none', background: 'transparent',
                            color: '#64748b', fontSize: 13, cursor: 'pointer',
                            transition: 'color 0.15s', textAlign: 'left',
                        }}
                        onMouseOver={e => e.currentTarget.style.color = '#f1f5f9'}
                        onMouseOut={e => e.currentTarget.style.color = '#64748b'}
                    >
                        <ArrowLeft size={16} />
                        <span>Back to Dashboard</span>
                    </button>
                </div>
            </aside>

            {/* ── Main Content ── */}
            <main style={{
                flex: 1, minWidth: 0, padding: '24px 40px 32px',
                overflowY: 'auto', maxHeight: '100vh',
            }}>
                {showFilterBar && (
                    <AnalyticsFilterBar
                        filters={filters}
                        onChange={updateFilters}
                        onRefresh={handleRefresh}
                        loading={false}
                    />
                )}
                {renderContent()}
            </main>
        </div>
    );
};


const NavButton = ({ item, isCurrent, onClick }) => {
    const Icon = item.icon;
    return (
        <button
            onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', borderRadius: 8, border: 'none',
                fontSize: 13, fontWeight: isCurrent ? 600 : 400,
                cursor: 'pointer', transition: 'all 0.15s',
                background: isCurrent ? '#1e293b' : 'transparent',
                color: isCurrent ? '#f1f5f9' : '#94a3b8',
                textAlign: 'left', width: '100%',
            }}
            onMouseOver={e => {
                if (!isCurrent) {
                    e.currentTarget.style.background = '#1e293b50';
                    e.currentTarget.style.color = '#cbd5e1';
                }
            }}
            onMouseOut={e => {
                if (!isCurrent) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#94a3b8';
                }
            }}
        >
            <Icon size={16} />
            <span>{item.label}</span>
        </button>
    );
};


export default AdminWorkspace;
